/* eslint-disable no-restricted-syntax, no-labels */

import storeManager from '../StoreManager';
import assessmentReader from './AssessmentReader';
import objectTypes, { CONTENT_PREFIX, QUESTION_PREFIX } from '../../constants/ObjectTypes';
import i18nService from '../../services/i18nService';
import sessionReader from './SessionReader';
import timeReader from './TimeReader';
import settingsReader from './SettingsReader';
import responseReader from './ResponseReader';
import xmlDomUtils from '../../qti/utils/XmlDomUtils';
import interactionParserUtils from '../../qti/parser/question/interactions/InteractionParserUtils';
import uiReader from './uiReader';
import customFieldsService from '../../services/CustomFieldsService';
import constants from '../../qti/Constants';
import marketplacePropertiesService from '../../services/MarketplacePropertiesService';
import { pbRenderThemes } from '../../constants/UIConstants';
import customFieldsConstants from '../../constants/CustomFieldsConstants';
import marketPlaces from '../../constants/MarketPlaces';

const questionReader = {
    /**
     * Returns the total number of questions excluding documents in all sections from the state
     * @param {object} state
     * @returns {int}
     */
    getTotalNumberOfQuestions(state = storeManager.getState()) {
        return state.toc.numberOfQuestions;
    },

    isQuestionReady(question, itemResult, isDocument) {
        if (!question) {
            return false;
        }

        const state = storeManager.getState();
        const language = this.getContentLanguageToUse(
            i18nService.getContentLanguageCode(state.session.language),
            question.supportedLanguages,
            question.defaultLanguage,
            assessmentReader.getSupportedLanguages(),
        );

        if (isDocument) {
            return question.isParsed && question.language === language;
        }

        return !!(question.isParsed
            && question.language === language
            && itemResult);
    },

    questionIsReadOnly(question, itemResult) {
        if (
            ((sessionReader.isContextPreview() || sessionReader.isContextReadinessCheck()) && itemResult && itemResult.outcomeVariables)
            || (sessionReader.isContextPreview() && sessionReader.isLayoutParamShowCorrectAnswers())
            || sessionReader.isViewMediaPrint()
            || sessionReader.isViewMediaPdf()
        ) {
            return true;
        }

        // For public exams upload questions are always shown as read only
        const isUpload = question && question.objectType === objectTypes.UPLOAD;
        if (sessionReader.isPublicExam() && isUpload) {
            return true;
        }

        return !sessionReader.isInteractive() || timeReader.timeHasExpired();
    },

    // TODO: refactor this method
    shouldShowCheckAnswer(isReadOnly, isManuallyScored, isDocument, isForm, questionType, customFields) {
        const isIntroductoryFrame = customFieldsService.isIntroductoryFrame(customFields);
        const isPreview = sessionReader.isContextPreviewAssessmentRun() &&
            !sessionReader.getPrintParam(sessionReader.printParamNames.SHOW_PREVIEW_WITH_FEEDBACK, false);

        if (isPreview
            || !sessionReader.isInteractive()
            || !sessionReader.isLayoutParamShowCheckAnswerButton()
        ) {
            return false;
        }
        return (
            sessionReader.isContextPreview()
            && !isReadOnly
            && !isDocument
            && !isForm
            && questionType !== objectTypes.SPEAKING
            && (!isManuallyScored
                || (isManuallyScored && settingsReader.isFeedbackEnabledInMarketplace()))
            && !isIntroductoryFrame
        );
    },

    isCKEditorBasedQuestion(question) {
        return [
            objectTypes.CODE_EDITOR,
            objectTypes.COMPILED_CODE_EDITOR,
            objectTypes.EXTENDED_TEXT,
        ].includes(question.objectType);
    },

    isExtendedText(question) {
        return question?.objectType === objectTypes.EXTENDED_TEXT;
    },

    /**
     * Returns the contentRevisionIds of all questions in a flat array
     * @param {object} state
     * @param {boolean} includeNonScoringItems - include non scoring items like documents and content blocks
     * @returns {Array}
     */
    getAllQuestionIdsFlat(state = storeManager.getState(), includeNonScoringItems = false) {
        const allQuestionsFlat = [];
        const { itemsOrder = [] } = state.toc;
        const { sections = [] } = state.assessment;

        itemsOrder.forEach((item) => {
            const sectionOfItem = sections[item.sectionIndex];

            if (!sectionOfItem) {
                // Likely caused by AssessmentActions.assessmentLoadSuccess dispatching assessment and toc update separately.
                console.info(`Unable to find section for item ${item.sectionIndex}`, item);
                return;
            }

            const question = sectionOfItem.questions[item.questionIndex];
            if (!question) {
                // Likely caused by AssessmentActions.assessmentLoadSuccess dispatching assessment and toc update separately.
                console.info(`Unable to find question ${item.questionIndex} in section`, sectionOfItem);
                return;
            }

            if (includeNonScoringItems) {
                allQuestionsFlat.push(question.contentRevisionId);
            } else if (
                question.objectType !== objectTypes.DOCUMENT &&
                question.objectType !== objectTypes.CONTENT_BLOCK
            ) {
                allQuestionsFlat.push(question.contentRevisionId);
            }
        });

        return allQuestionsFlat;
    },

    /**
     * Returns the wanted language if it is supported on the question and question set.
     * @param {string} wantedLanguage content language code ('no_no', 'no_no_ny', ...)
     * @param {Array} questionLanguages of the question
     * @param {string} defaultLanguage of the question
     * @param {Array} assessmentLanguages
     * @returns {string} The requested language code, if supported. Otherwise question.defaultLanguage is returned.
     */
    getContentLanguageToUse(wantedLanguage, questionLanguages = [], defaultLanguage, assessmentLanguages) {
        const initialLanguage = sessionReader.isAnnotationMode() ? defaultLanguage : wantedLanguage;

        const isValidContentLanguage = (language) => {
            return assessmentLanguages.includes(language)
                && questionLanguages.includes(language);
        };

        // Pri 1 - The wanted language
        if (isValidContentLanguage(initialLanguage)) {
            return initialLanguage;
        }
        // Pri 2 - Default language on the question
        if (isValidContentLanguage(defaultLanguage)) {
            return defaultLanguage;
        }

        // Pri 3 - The first supported language on the assessment that is available on the question.
        const acceptableLang = assessmentLanguages.find(lang => questionLanguages.includes(lang));
        if (acceptableLang) {
            return acceptableLang;
        }

        return defaultLanguage;
    },

    isDocument(question) {
        return !!(question && question.objectType === objectTypes.DOCUMENT);
    },

    isStimulus(question) {
        return !!(question && question.category === objectTypes.CATEGORY.STIMULUS);
    },

    isForm(question) {
        return question && (question.objectType === objectTypes.FORM);
    },

    isContentBlock(question) {
        return question && (question.objectType === objectTypes.CONTENT_BLOCK);
    },

    isInlineChoice(question) {
        return question && (question.objectType === objectTypes.INLINE_CHOICE);
    },

    isOralQuestion(question) {
        return question.objectType === objectTypes.ORAL;
    },

    isSpreadSheetQuestion(question) {
        if (question == null) {
            return false;
        }
        return question.objectType === objectTypes.SPREADSHEET;
    },

    isGapMatchQuestion(question) {
        return question.objectType === objectTypes.GAP_MATCH;
    },

    isGapMatchQuestionWithSingleInteraction(question) {
        if (!question || marketplacePropertiesService.getUiTheme()?.toLowerCase() !== marketPlaces.ceq) return false;
        const { responseModels, responseDeclarations } = question;
        const hasSingleResponseModel = responseModels?.length === 1 || responseDeclarations?.length === 1;
        return hasSingleResponseModel && question.objectType === objectTypes.GAP_MATCH;
    },

    isGapMatchQuestionWithMultipleInteractions(question) {
        if (!marketplacePropertiesService.isEnableTwoInlineGapMatchAreas() || !question) return false;
        const { responseModels, responseDeclarations } = question;
        if (responseModels?.length > 1) {
            const areNotGapModels = responseModels.some(model => model.type !== constants.interactionType.GAP_MATCH_INTERACTION);
            return !areNotGapModels;
        }
        // fallback in case responseModels are not available
        return responseDeclarations?.length > 1 && question.objectType === objectTypes.GAP_MATCH;
    },

    isGraphicGapMatchQuestion(question) {
        return question.objectType === objectTypes.GRAPHIC_GAP_MATCH;
    },

    isSpeaking(question) {
        return question.objectType === objectTypes.SPEAKING;
    },

    isCurrentlyShown(question) {
        const { questionId } = storeManager.getState().currentlyShown;
        return question.contentRevisionId === questionId;
    },

    canHaveResponse(question) {
        return !this.isDocument(question) &&
               !this.isOralQuestion(question) &&
               !this.isContentBlock(question) &&
               !this.isSpreadSheetQuestion(question);
    },

    canHaveResult(question) {
        return !!question && !this.isDocument(question) && !this.isForm(question) && !this.isContentBlock(question);
    },

    hasEssayToolbarPreset(question) {
        return question.presets && question.presets.essayToolbar && question.presets.essayToolbar.textContent;
    },

    getEssayToolbarPreset(question) {
        if (this.hasEssayToolbarPreset(question)) {
            return question.presets.essayToolbar.textContent;
        }

        return undefined;
    },

    hasEssaySpecialCharsPreset(question) {
        return question.presets && question.presets.essaySpecialChars && question.presets.essaySpecialChars.textContent;
    },

    getEssaySpecialCharsPreset(question) {
        if (this.hasEssaySpecialCharsPreset(question)) {
            return question.presets.essaySpecialChars.textContent;
        }

        return undefined;
    },

    hasMathSymbolPalettePreset(question) {
        return question.presets && question.presets.mathSymbolPalette && question.presets.mathSymbolPalette.textContent;
    },

    getMathSymbolPalettePreset(question) {
        if (this.hasMathSymbolPalettePreset(question)) {
            return question.presets.mathSymbolPalette.textContent;
        }

        return undefined;
    },

    isAdaptiveThresholdStep() {
        if (sessionReader.isQuestionItemBankLevel()) {
            return this.isQuestionLevelAdaptiveThresholdStep();
        } else if (sessionReader.isSectionItemBankLevel()) {
            return this.isSectionLevelAdaptiveThresholdStep();
        }
        return false;
    },

    isQuestionLevelAdaptiveThresholdStep() {
        const currentQuestion = assessmentReader.getCurrentQuestion();

        if (currentQuestion.contentRevisionId) {
            const catAdditionalResources = settingsReader.getCatTestAdditionalResources();
            const adaptiveSteps = JSON.parse(catAdditionalResources).adaptiveSteps;

            return adaptiveSteps.some(step => step === currentQuestion.questionNumberInQuestionSet);
        }

        return false;
    },

    /**
     * Returns true when TT is in the last question of a section considered as step.
     * Note the "step" is based on the presentational order (index + 1).
     * @returns {boolean}
     */
    isSectionLevelAdaptiveThresholdStep() {
        const currentQuestionId = storeManager.getState().currentlyShown.questionId;

        if (responseReader.isQuestionDone(assessmentReader.getQuestion(currentQuestionId))) {
            const catAdditionalResources = settingsReader.getCatTestAdditionalResources();
            const adaptiveSteps = JSON.parse(catAdditionalResources).adaptiveSteps;
            const currentSection = assessmentReader.getCurrentSection();
            const currentSectionIndex = assessmentReader.getCurrentShownSectionIndex();

            const lastQuestionInSection = currentSection.questions[currentSection.questions.length - 1];
            const isSectionAdaptiveStep = adaptiveSteps.some(step => step === currentSectionIndex + 1);

            if (isSectionAdaptiveStep && (lastQuestionInSection.contentRevisionId === currentQuestionId)) {
                return true;
            }
        }

        return false;
    },

    hasVisibleContent(question) {
        if (!question?.itemBody) {
            return false;
        }

        const itemBodyElement = xmlDomUtils.getElementFromString(question.itemBody);
        const content = interactionParserUtils.getNodeContent(itemBodyElement);
        return !!content && content.length > 0;
    },

    hasDynamicRubricSupport({ objectType }) {
        return [
            objectTypes.EXTENDED_TEXT,
            objectTypes.GAP_MATCH,
            objectTypes.INLINE_CHOICE,
            objectTypes.MATCHING,
            objectTypes.MULTIPLE_CHOICE,
            objectTypes.TEXT_ENTRY,
            objectTypes.WORD_FORMATION,
        ].includes(objectType);
    },

    hasStimulusDynamicRubricSupport({ objectType }) {
        const supportedTypes = [
            objectTypes.EXTENDED_TEXT,
            objectTypes.MULTIPLE_CHOICE,
        ];

        if (settingsReader.isPBRenderForCeqEnabled()) {
            supportedTypes.push(objectTypes.INLINE_CHOICE);
        }
        return supportedTypes.includes(objectType);
    },

    isResettableQuestion({ objectType }) {
        return [
            objectTypes.COMPOSITE,
            objectTypes.FORM,
            objectTypes.GAP_MATCH,
            objectTypes.GRAPHIC_TEXT_ENTRY,
        ].includes(objectType);
    },

    isExtendedRenderingActivated(question) {
        return !!question.responseModels?.[0]?.extendedRenderingActivated;
    },

    isWordFormationQuestion(question) {
        return objectTypes.WORD_FORMATION === question.objectType;
    },

    isMatchingQuestion(question) {
        return objectTypes.MATCHING === question.objectType;
    },

    isMultipleResponseQuestion(question) {
        return objectTypes.MULTIPLE_RESPONSE === question.objectType;
    },

    isMultipleChoiceQuestion(question) {
        return objectTypes.MULTIPLE_CHOICE === question.objectType;
    },

    /**
     * Checks if a question contains an image in its itemBody or mediaModels.
     * Used for paper-based rendering to determine layout positioning.
     * @param {Object} question - The question model
     * @returns {boolean} - True if the question contains an image
     */
    hasImageInQuestion(question) {
        if (!question) {
            return false;
        }
        // Check if question has image in mediaModels
        const hasImageModel = question.mediaModels?.some(mediaModel => mediaModel.type === 'image');
        if (hasImageModel) {
            return true;
        }
        // Check if question has image tag in itemBody
        if (question.itemBody) {
            const regex = /<img/gi;
            return regex.test(question.itemBody);
        }
        return false;
    },

    /**
     * Checks if MCQ has image options (alternatives with isImage flag)
     * Used to distinguish MCQs with images in options vs images in question body
     * @param {Object} question - The question model
     * @returns {boolean} - True if MCQ has image alternatives
     */
    hasImageOptions(question) {
        if (!question || !question.responseModels) {
            return false;
        }
        return question.responseModels.some(responseModel =>
            responseModel.alternatives?.some(alt => alt.isImage),
        );
    },

    isCompiledCodeEditor(question) {
        return objectTypes.COMPILED_CODE_EDITOR === question.objectType;
    },

    isInvertedMatchingResponseModel(question, responseIdentifier) {
        if (!this.isMatchingQuestion(question)) {
            return false;
        }

        const responseModel = question.responseModels.find(model => model.identifier === responseIdentifier);
        if (!responseModel) {
            return false;
        }

        return this.isInvertedMatchingAlternatives(responseModel.alternatives);
    },

    isInvertedMatchingAlternatives(alternatives) {
        const [matchRows, matchColumns] = alternatives;

        const rowsMatchMax = parseInt(matchRows[0]?.matchMax, 10);
        const columnsMatchMax = parseInt(matchColumns[0]?.matchMax, 10);

        if (isNaN(rowsMatchMax) || isNaN(columnsMatchMax)) {
            return false;
        }

        return (rowsMatchMax > 1) && (columnsMatchMax === 1);
    },

    isHorizontalTokenPosition(question) {
        const tokenPosition = question.responseModels?.[0]?.tokenPosition;
        return ['right', 'left'].includes(tokenPosition);
    },

    isSplitQuestionView(question) {
        return !!question
            && this.isGapMatchQuestion(question)
            && this.isHorizontalTokenPosition(question)
            && this.isExtendedRenderingActivated(question)
            && !sessionReader.isViewMediaPrintOrPdf()
            && uiReader.supportsSplitScrolling();
    },

    shouldCommitAnswerTimestamp(question) {
        return !!question && sessionReader.isCommittingAnswerSupported(true) && !sessionReader.isContextReadinessCheck();
    },

    shouldAutocommitAnswer(question) {
        if (!question || !this.canHaveResult(question)) {
            return false;
        }

        return uiReader.isAnsweredQuestionsAutocommitEnabled() && responseReader.isQuestionAttempted(question);
    },

    getNextQuestionContentRevisionId(questionId) {
        const state = storeManager.getState();
        const testQuestions = state.testQuestions;
        return testQuestions[questionId] ? testQuestions[questionId].nextQuestionId : null;
    },

    isFirstQuestionInSection(question) {
        return question?.questionNumberingInSection === '1' ||
               question?.questionNumberingInSection === undefined;
    },

    getQuestionType(question) {
        if (!question?.objectType) return null;
        if (question.objectType.startsWith(QUESTION_PREFIX)) {
            return question.objectType.substring(QUESTION_PREFIX.length);
        }
        if (this.isDocument(question)) {
            return question.objectType.substring(CONTENT_PREFIX.length);
        }
        return null;
    },

    getClassNameBasedOnMetadata(question) {
        const pbClassNames = [];
        const isPaperBased = sessionReader.isPrintStylePaperBased();
        const pbRenderTheme = marketplacePropertiesService.getPbRenderStyle()?.toLowerCase();
        if (!(isPaperBased && question && pbRenderTheme)) return [];
        const themeName = pbRenderThemes.includes(pbRenderTheme) ? pbRenderTheme : null;
        const questionType = this.getQuestionType(question);
        if (!themeName || !questionType) return [];
        pbClassNames.push(`${themeName}-pb`, questionType);
        const metadata = customFieldsService.getCustomFieldValue(
            question?.customFields,
            customFieldsConstants.PAPER_BASED_RENDER_OPTIONS
        );
        if (metadata && typeof metadata === 'string') {
            const formattedMetadata = metadata.trim().toLowerCase().replace(/\s+/g, '-');
            if (formattedMetadata) {
                pbClassNames.push(formattedMetadata);
            }
        }
        return pbClassNames;
    },
};

export default questionReader;
